import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { Page, Pageable } from '../../../utils/pageable';
import { IosBridgeBase } from '../../../mobile/api-services/bridge/ios-bridge-base';
import {
  CachedHttpRequest,
  HttpMethod,
  Platform,
} from '../../../constraints';
import {
  AnswerHolderBase,
  ExerciseSessionByPathRequest,
  ExerciseSessionRecreateRequest,
  ExerciseSessionStatus,
  ItemAvailability,
  V3Chapter,
  V3ExamSession,
  V3ExamSessionPart,
  V3ExamSessionPartStatus,
  V3ExamSessionStatus,
  V3ExerciseSession,
  V3ExerciseSessionQuestion,
  V3ExerciseSet,
} from '../../model/v3/cspa';
import { CspaRestV3 } from './cspa-rest-v3';
import { LogsService } from '../../../utils/services/logs.service';

@Injectable({
  providedIn: 'root',
})
export class CspaRestV3IosService implements CspaRestV3 {
  constructor(
    private bridge: IosBridgeBase,
    private logger: LogsService,
  ) {}

  private peopleUrl(personId: number, path = ''): string {
    return `/v1/people/${personId}${path}`;
  }

  private eventsUrl(path: string): string {
    return `/v1${path}`;
  }

  private appendParams(url: string, key: string, value: string, isFirst: boolean): string {
    const separator = isFirst ? '?' : '&';
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`;
  }

  private params(url: string, params: HttpParams): string {
    if (!params || params.keys().length === 0) {
      return url;
    }
    let result = url;
    let isFirst = true;
    for (const key of params.keys()) {
      const value = params.get(key);
      if (value == null) {
        continue;
      }
      result = this.appendParams(result, key, value, isFirst);
      isFirst = false;
    }
    return result;
  }

  private exchange<T>(method: HttpMethod, path: string, body?: unknown): Observable<T> {
    const request = new CachedHttpRequest(
      Platform.Events,
      method,
      path,
      null,
      body == null ? null : JSON.stringify(body),
      null,
      null,
    );
    return this.bridge.callForResponse<T>('exchange', { request });
  }

  isOffline(): Observable<boolean> {
    return this.bridge.callForResponse<boolean>('isOffline', {}).pipe(
      timeout(2000),
      catchError(err => {
        this.logger.log(`isOffline bridge call failed/timed out, assuming online. error=${err}`);
        return of(false);
      }),
    );
  }

  listExerciseSets(): Observable<V3ExerciseSet[]> {
    this.logger.log('cspa (v3) listExerciseSets');
    return this.exchange<V3ExerciseSet[]>(HttpMethod.GET, this.eventsUrl('/struct/sets'));
  }

  getChapters(path: string, depth: number): Observable<V3Chapter[]> {
    this.logger.log(`cspa (v3) getChapters path=${path} depth=${depth}`);
    const params = new HttpParams().set('depth', depth.toString());
    return this.exchange<V3Chapter[]>(
      HttpMethod.GET,
      this.params(this.eventsUrl(`/struct/${encodeURIComponent(path)}`), params),
    );
  }

  listPersonAvailabilities(
    personId: number,
    path?: string | null,
    depth?: number | null,
  ): Observable<ItemAvailability[]> {
    this.logger.log(`cspa (v3) listPersonAvailabilities personId=${personId}`);
    let params = new HttpParams();
    if (path != null) {
      params = params.set('path', path);
    }
    if (depth != null) {
      params = params.set('depth', depth.toString());
    }
    return this.exchange<ItemAvailability[]>(
      HttpMethod.GET,
      this.params(this.peopleUrl(personId, '/exercise-progress-tree'), params),
    );
  }

  findSessions(
    personId: number,
    pathPrefix?: string | null,
    pageable?: Pageable,
  ): Observable<Page<V3ExerciseSession>> {
    this.logger.log(`cspa (v3) findSessions personId=${personId}`);
    let params = pageable ? pageable.appendParams(new HttpParams()) : new HttpParams();
    if (pathPrefix != null) {
      params = params.set('pathPrefix', pathPrefix);
    }
    return this.exchange<Page<V3ExerciseSession>>(
      HttpMethod.GET,
      this.params(this.peopleUrl(personId, '/exercise-sessions'), params),
    );
  }

  createSession(personId: number, request: ExerciseSessionByPathRequest): Observable<V3ExerciseSession> {
    this.logger.log(`cspa (v3) createSession personId=${personId}`);
    return this.exchange<V3ExerciseSession>(
      HttpMethod.POST,
      this.peopleUrl(personId, '/exercise-sessions'),
      request,
    );
  }

  recreateSession(personId: number, request: ExerciseSessionRecreateRequest): Observable<V3ExerciseSession> {
    this.logger.log(`cspa (v3) recreateSession personId=${personId}`);
    return this.exchange<V3ExerciseSession>(
      HttpMethod.POST,
      `${this.peopleUrl(personId, '/exercise-sessions')}?recreate=`,
      request,
    );
  }

  getSession(personId: number, uuid: string): Observable<V3ExerciseSession> {
    this.logger.log(`cspa (v3) getSession personId=${personId} uuid=${uuid}`);
    return this.exchange<V3ExerciseSession>(
      HttpMethod.GET,
      this.peopleUrl(personId, `/exercise-sessions/${uuid}`),
    );
  }

  getSessionQuestions(personId: number, uuid: string): Observable<V3ExerciseSessionQuestion[]> {
    this.logger.log(`cspa (v3) getSessionQuestions personId=${personId} uuid=${uuid}`);
    return this.exchange<V3ExerciseSessionQuestion[]>(
      HttpMethod.GET,
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/questions`),
    );
  }

  updateSessionStatus(
    personId: number,
    uuid: string,
    status: ExerciseSessionStatus,
  ): Observable<V3ExerciseSession> {
    this.logger.log(`cspa (v3) updateSessionStatus personId=${personId} uuid=${uuid}`);
    return this.exchange<V3ExerciseSession>(
      HttpMethod.PATCH,
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/status`),
      status,
    );
  }

  submitQuestionAnswer(
    personId: number,
    uuid: string,
    questionNumber: number,
    answer: AnswerHolderBase,
  ): Observable<void> {
    this.logger.log(`cspa (v3) submitQuestionAnswer personId=${personId} uuid=${uuid} q=${questionNumber}`);
    return this.exchange<void>(
      HttpMethod.POST,
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/questions/${questionNumber}/answer`),
      answer,
    );
  }

  findExamSessions(personId: number, pathPrefix?: string | null): Observable<V3ExamSession[]> {
    this.logger.log(`cspa (v3) findExamSessions personId=${personId}`);
    let params = new HttpParams();
    if (pathPrefix != null) {
      params = params.set('pathPrefix', pathPrefix);
    }
    return this.exchange<V3ExamSession[]>(
      HttpMethod.GET,
      this.params(this.peopleUrl(personId, '/exam-sessions'), params),
    );
  }

  createExamSession(personId: number, request: { path: string }): Observable<V3ExamSession> {
    this.logger.log(`cspa (v3) createExamSession personId=${personId}`);
    return this.exchange<V3ExamSession>(
      HttpMethod.POST,
      this.peopleUrl(personId, '/exam-sessions'),
      request,
    );
  }

  getExamSession(personId: number, uuid: string): Observable<V3ExamSession> {
    this.logger.log(`cspa (v3) getExamSession personId=${personId} uuid=${uuid}`);
    return this.exchange<V3ExamSession>(
      HttpMethod.GET,
      this.peopleUrl(personId, `/exam-sessions/${uuid}`),
    );
  }

  updateExamSessionStatus(
    personId: number,
    uuid: string,
    status: V3ExamSessionStatus,
  ): Observable<V3ExamSession> {
    this.logger.log(`cspa (v3) updateExamSessionStatus personId=${personId} uuid=${uuid}`);
    return this.exchange<V3ExamSession>(
      HttpMethod.PATCH,
      this.peopleUrl(personId, `/exam-sessions/${uuid}/status`),
      status,
    );
  }

  findExamSessionsParts(personId: number, uuid: string): Observable<V3ExamSessionPart[]> {
    this.logger.log(`cspa (v3) findExamSessionsParts personId=${personId} uuid=${uuid}`);
    return this.exchange<V3ExamSessionPart[]>(
      HttpMethod.GET,
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts`),
    );
  }

  getPartQuestions(personId: number, uuid: string, partNum: number): Observable<V3ExerciseSessionQuestion[]> {
    this.logger.log(`cspa (v3) getPartQuestions personId=${personId} uuid=${uuid} part=${partNum}`);
    return this.exchange<V3ExerciseSessionQuestion[]>(
      HttpMethod.GET,
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/questions`),
    );
  }

  updatePartStatus(
    personId: number,
    uuid: string,
    partNum: number,
    status: V3ExamSessionPartStatus,
  ): Observable<V3ExamSessionPart> {
    this.logger.log(`cspa (v3) updatePartStatus personId=${personId} uuid=${uuid} part=${partNum}`);
    return this.exchange<V3ExamSessionPart>(
      HttpMethod.PATCH,
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/status`),
      status,
    );
  }

  submitPartQuestionAnswer(
    personId: number,
    uuid: string,
    partNum: number,
    questionNumber: number,
    answer: AnswerHolderBase,
  ): Observable<void> {
    this.logger.log(`cspa (v3) submitPartQuestionAnswer personId=${personId} uuid=${uuid} part=${partNum} q=${questionNumber}`);
    return this.exchange<void>(
      HttpMethod.PUT,
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/questions/${questionNumber}/answer`),
      answer,
    );
  }
}
